import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ActionButton, FormField, inputClass } from "../components/Primitives";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * Request-password-reset page. Sends {email} to the backend which ALWAYS
 * responds 200 (whether or not the email is on file — no existence oracle).
 * Success screen doesn't say "we sent an email to X" — instead it says
 * "if an account exists, we've sent a link" to preserve the same guarantee.
 */
export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim() });
      setSent(true);
    } catch {
      // Backend returns 400 only for a malformed email — everything else is 200.
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-8">
      <div className="max-w-md w-full border border-[#27272A] bg-[#121212]" data-testid="forgot-password-page">
        <div className="border-b border-[#27272A] px-8 py-6">
          <div className="flex items-center gap-3 mb-4">
            <KeyRound className="size-6 text-[#FF3B30]" />
            <div className="label-mute">Iris Security · Password Reset</div>
          </div>
          <div className="font-display text-2xl uppercase font-bold tracking-tight">
            {sent ? "Check your email" : "Reset your password"}
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            {sent
              ? "If an account matches that email, a reset link has been sent."
              : "We'll email you a link to choose a new one."}
          </div>
        </div>
        <div className="p-8 space-y-5">
          {sent ? (
            <>
              <p className="text-xs text-zinc-500 leading-relaxed">
                The link expires in 30 minutes and can only be used once. Didn&apos;t receive it? Check spam or wait a minute and try again.
              </p>
              <button
                onClick={() => nav("/login")}
                className="text-xs uppercase tracking-widest text-[#FF3B30] hover:text-white"
                data-testid="forgot-back-to-login"
              >
                ← Return to login
              </button>
            </>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <FormField label="Email">
                <input
                  type="email"
                  data-testid="forgot-email-input"
                  className={inputClass}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                />
              </FormField>
              <ActionButton type="submit" disabled={submitting} data-testid="forgot-submit-button">
                {submitting ? "Sending…" : "Send reset link"}
              </ActionButton>
              <div className="text-center pt-2">
                <Link to="/login" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white">
                  ← Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
